import { clientEnvModule } from "@/modules/client-env";
import { authApi } from "@/modules/api";
import { windowModule } from "@/modules/window";
import { BaseError } from "@/domains/errors";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { toastModule } from "@/modules/toast";

type AppleAuthorizationPlatform = "web" | "desktop";

/**
 * Starts the Apple sign-in flow by asking the backend for the authorization URL.
 * The backend creates the OAuth state, stores the nonce in an HttpOnly cookie,
 * and returns the Apple redirect target for this client platform.
 */
export const appleIdentityTriggerLogIn = async ({
  includeNativeChatOnboarding = true,
}: {
  includeNativeChatOnboarding?: boolean;
} = {}) => {
  const platform: AppleAuthorizationPlatform = clientEnvModule.isDesktop() ? "desktop" : "web";

  try {
    const result = await authApi.get("/v2/auth/apple/authorization-url", {
      params: {
        query: {
          platform,
          include_native_chat_onboarding: includeNativeChatOnboarding,
        },
      },
    });

    const authorizationUrl = result.data?.authorization_url;

    if (!authorizationUrl) {
      throw new BaseError({
        message: "[appleIdentityTriggerLogIn] Missing Apple authorization URL.",
      });
    }

    await windowModule.navigateToExternal({
      url: new URL(authorizationUrl),
    });
  } catch (unknownErr) {
    toastModule.triggerToast({
      content: "Failed to start Apple sign in. Please try again.",
    });

    logger.error({
      message: "[appleIdentityTriggerLogIn] Failed to start Apple sign in.",
      info: {
        err: objectModule.safeErrorAsJson(unknownErr as BaseError),
        platform,
      },
    });
  }
};
